/**
 * 주문 관련 훅 공통 유틸리티
 *
 * `useOrders`, `useOrder`, `useOrderStatus`, `useGuestOrders`,
 * `useCreateOrder`, `useCancelOrder`에서 공유하는 상수와 헬퍼.
 */
import { ApiClientError, getErrorMessage } from '@/lib/api';

// ==================== 상수 ====================
export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_POLLING_INTERVAL = 5000;
export const MAX_RETRIES = 2;
export const RETRY_DELAY = 1000;
export const JITTER_MAX = 200; // 최대 200ms 지터

// ==================== 공통 유틸리티 ====================

/**
 * API 에러에서 사용자 친화적 메시지 추출
 */
export function getApiErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof ApiClientError) {
    switch (err.status) {
      case 0:
      case 408:
        return '네트워크 연결을 확인해주세요.';
      case 429:
        return '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.';
      case 403:
        return '비밀번호가 올바르지 않습니다.';
      case 404:
        return '주문을 찾을 수 없습니다.';
      default:
        return getErrorMessage(err, defaultMessage);
    }
  }
  return getErrorMessage(err, defaultMessage);
}

/**
 * 재시도 가능한 에러인지 확인
 */
export function isRetryableError(err: unknown): boolean {
  if (err instanceof ApiClientError) {
    return [0, 408, 429, 500, 502, 503, 504].includes(err.status);
  }
  return false;
}

/**
 * 재시도 로직이 포함된 fetch 래퍼
 * - 지수 백오프 + Jitter로 thundering herd 방지
 * - AbortSignal로 요청 취소 지원
 * - shouldCancel 콜백으로 추가 취소 조건 지원
 */
export async function fetchWithRetry<T>(
  fn: (signal?: AbortSignal) => Promise<T>,
  maxRetries: number = MAX_RETRIES,
  shouldCancel?: () => boolean,
  signal?: AbortSignal
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // AbortSignal 체크
    if (signal?.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }

    // 취소 체크
    if (shouldCancel?.()) {
      throw new Error('Request cancelled');
    }

    try {
      return await fn(signal);
    } catch (error) {
      // AbortError는 재시도하지 않음
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      lastError = error;
      if (attempt < maxRetries && isRetryableError(error)) {
        // 지수 백오프 + Jitter (thundering herd 방지)
        const jitter = Math.random() * JITTER_MAX;
        const delay = RETRY_DELAY * Math.pow(2, attempt) + jitter;
        await new Promise((resolve) => setTimeout(resolve, delay));

        // 재시도 전 취소 체크
        if (signal?.aborted || shouldCancel?.()) {
          throw new DOMException('Request aborted', 'AbortError');
        }
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}
